import React, { useState, useEffect } from 'react'
import { useProgramContext } from '@/context/ProgramContext'
import { useCreateWebhook, useUpdateWebhook } from '@/hooks/useWebhooks'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Badge } from '@/components/ui/badge'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { 
  Webhook, 
  Bug, 
  Lightbulb
} from 'lucide-react'
import { useTranslation } from '@/hooks/useTranslation'

interface WebhookFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  webhook?: any // For editing existing webhook
}

const getWebhookFormSchema = (t: (key: string) => string) => z.object({
  name: z.string()
    .min(1, t('webhookForm.webhookNameRequired'))
    .max(255, t('webhookForm.nameCannotExceed255')),
  url: z.string()
    .refine(
      (url) => {
        if (url === '') return true
        return url.startsWith('http://') || url.startsWith('https://')
      },
      t('webhookForm.onlyHttpHttpsAllowed')
    )
    .refine(
      (url) => url === '' || url.length <= 2048,
      t('webhookForm.urlCannotExceed2048')
    )
    .refine(
      (url) => {
        if (url === '') return true
        return z.string().url().safeParse(url).success
      },
      t('webhookForm.pleaseEnterValidUrl')
    ),
  event_types: z.array(z.string())
    .min(1, t('webhookForm.atLeastOneEventTypeRequired'))
    .max(4, t('webhookForm.cannotSelectMoreThan4')),
  is_active: z.boolean().default(true)
})
.refine(
  (data) => {
    // In create mode, URL is required
    // We'll determine create mode by checking if URL is provided
    // This is a bit hacky but works for our use case
    return true // We'll handle the required validation in the component
  }
)

type WebhookFormData = z.infer<ReturnType<typeof getWebhookFormSchema>>

const getEventTypes = (t: (key: string) => string) => [
  {
    id: 'new_bug_report',
    label: t('webhookForm.bugReports'),
    description: t('webhookForm.triggeredWhenBugReportSubmitted'),
    icon: Bug,
    color: 'text-red-600'
  },
  {
    id: 'new_feature_request',
    label: t('webhookForm.featureRequests'),
    description: t('webhookForm.triggeredWhenFeatureRequestCreated'),
    icon: Lightbulb,
    color: 'text-blue-600'
  }
]

export function WebhookForm({ open, onOpenChange, webhook }: WebhookFormProps) {
  const { t } = useTranslation()
  const { programId } = useProgramContext()
  const createWebhookMutation = useCreateWebhook()
  const updateWebhookMutation = useUpdateWebhook()
  const [testUrl, setTestUrl] = useState('')

  const isEditing = !!webhook
  const isLoading = createWebhookMutation.isPending || updateWebhookMutation.isPending
  const webhookFormSchema = getWebhookFormSchema(t)
  const eventTypes = getEventTypes(t)

  const form = useForm<WebhookFormData>({
    resolver: zodResolver(webhookFormSchema),
    defaultValues: {
      name: '',
      url: '',
      event_types: ['new_bug_report'], // Default to bug reports
      is_active: true
    }
  })

  useEffect(() => {
    if (webhook) {
      form.reset({
        name: webhook.name,
        url: '',
        event_types: webhook.event_types,
        is_active: webhook.is_active
      })
      setTestUrl(webhook.name + ' (' + t('webhookForm.currentUrlIsEncrypted') + ')')
    } else {
      form.reset({
        name: '',
        url: '',
        event_types: ['new_bug_report'],
        is_active: true
      })
      setTestUrl('')
    }
  }, [webhook, form, t])

  const onSubmit = async (data: WebhookFormData) => {
    if (!programId) return

    if (!isEditing && !data.url) {
      form.setError('url', { message: t('webhookForm.webhookUrlRequired') })
      return
    }

    try {
      if (isEditing) {
        await updateWebhookMutation.mutateAsync({
          programId,
          webhookId: webhook.id,
          data: {
            name: data.name,
            ...(data.url ? { url: data.url } : {}), // Only include URL if provided
            event_types: data.event_types,
            is_active: data.is_active
          }
        })
      } else {
        await createWebhookMutation.mutateAsync({
          programId,
          data
        })
      }
      
      onOpenChange(false)
      form.reset()
      setTestUrl('')
    } catch (error) {
      // Error is handled by the mutation hooks
    }
  }

  const handleEventTypeChange = (eventType: string, checked: boolean) => {
    const currentTypes = form.getValues('event_types')
    
    if (checked) {
      if (!currentTypes.includes(eventType)) {
        form.setValue('event_types', [...currentTypes, eventType], { shouldValidate: true })
      }
    } else {
      if (currentTypes.includes(eventType)) {
        form.setValue('event_types', currentTypes.filter(type => type !== eventType), { shouldValidate: true })
      }
    }
  }

  const testUrlError = testUrl && testUrl !== webhook?.name + ' (' + t('webhookForm.currentUrlIsEncrypted') + ')' ? 
    !testUrl.match(/^https?:\/\/.+/) ? t('webhookForm.pleaseEnterValidHttpHttpsUrl') : null : null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Webhook className="w-5 h-5" />
            {isEditing ? t('webhookForm.editWebhook') : t('webhookForm.createWebhook')}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? t('webhookForm.updateWebhookSettings') : t('webhookForm.setUpWebhookNotifications')}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('webhookForm.name')}</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder={t('webhookForm.namePlaceholder')}
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('webhookForm.url')}
                      {isEditing && (
                        <Badge variant="secondary" className="ml-2 text-xs">
                          {t('webhookForm.hiddenForSecurity')}
                        </Badge>
                      )}
                    </FormLabel>
                    <FormControl>
                      <Input 
                        placeholder={isEditing 
                          ? t('webhookForm.currentUrlEncryptedHidden')
                          : t('webhookForm.urlPlaceholder')
                        } 
                        {...field} 
                      />
                    </FormControl>
                    {isEditing && (
                      <p className="text-xs text-gray-600">
                        {t('webhookForm.webhookUrlEncryptedSecurely')}
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

            </div>

            {/* Event Types */}
            <FormField
              control={form.control}
              name="event_types"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-semibold">{t('webhookForm.events')}</FormLabel>
                  <div className="grid grid-cols-2 gap-3">
                    {eventTypes.map((eventType) => {
                      const IconComponent = eventType.icon
                      const isChecked = field.value.includes(eventType.id)
                      
                      return (
                        <label 
                          key={eventType.id}
                          htmlFor={eventType.id}
                          className={`flex items-center space-x-3 p-3 border-2 rounded-lg cursor-pointer transition-all hover:border-gray-300 ${
                            isChecked ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                          }`}
                        >
                          <Checkbox
                            id={eventType.id}
                            checked={isChecked}
                            onCheckedChange={(checked) => 
                              handleEventTypeChange(eventType.id, checked as boolean)
                            }
                          />
                          <div className="flex items-center gap-2 flex-1">
                            <IconComponent className={`w-4 h-4 ${eventType.color}`} />
                            <span className="text-sm font-medium">
                              {eventType.label}
                            </span>
                          </div>
                        </label>
                      )
                    })}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Settings */}
            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <FormLabel className="text-sm font-medium">{t('webhookForm.enableWebhook')}</FormLabel>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />


            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                {t('webhookForm.cancel')}
              </Button>
              <Button 
                type="submit" 
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    {isEditing ? t('webhookForm.updating') : t('webhookForm.creating')}
                  </>
                ) : (
                  isEditing ? t('webhookForm.updateWebhook') : t('webhookForm.createWebhook')
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}